import React, { useContext, useEffect, useState } from 'react';
import { EllipsisOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Dropdown, Layout, Menu, Modal, PageHeader, Table, Typography } from 'antd';
import { apiClient, apiErrorMessage, hasPermission } from '../../../utils';
import { Location } from '../../../models';
import { EntityChangeLog } from '../../reports';
import { UserContext } from '../../contexts';
import { LocationEditPage } from './location-edit-page';

export function LocationsPage() {
  const { Content } = Layout;
  const { Text } = Typography;

  const { user } = useContext(UserContext);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalLocation, setEditModalLocation] = useState<Location>();
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState<Location[]>();
  const [changeLogModalOpen, setChangeLogModalOpen] = useState<string>();
  const [canEdit, setCanEdit] = useState(false);

  useEffect(() => {
    loadLocations();
  }, []);

  useEffect(() => {
    setCanEdit(hasPermission(user, 'locations.edit'));
  }, [user]);

  const loadLocations = () => {
    setLoading(true);
    apiClient
      .get('locations')
      .then(response => {
        setLocations(response.data as Location[]);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const openEditModalByLocation = (location: Location) => {
    setEditModalLocation(location);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
  };

  const openChangeLogModal = (id: string) => {
    setChangeLogModalOpen(id);
  };

  const closeChangeLogModal = () => {
    setChangeLogModalOpen(undefined);
  };

  const onRefreshClick = () => {
    loadLocations();
  };

  const onUserAdded = () => {
    closeEditModal();
    loadLocations();
  };

  const onTableActionClick = (e: any, id: string) => {
    if (e.key === 'edit') {
      const location = locations?.find(m => m.id === id);
      if (location) {
        openEditModalByLocation(location);
      }
    } else if (e.key === 'changelog') {
      openChangeLogModal(id);
    }
  };

  const tableActions = (id: string) => (
    <Menu onClick={(e: any) => onTableActionClick(e, id)}>
      {canEdit && (
        <Menu.Item key="edit">
          <Text>Düzenle</Text>
        </Menu.Item>
      )}
      <Menu.Item key="changelog">
        <Text>Değişim geçmişi</Text>
      </Menu.Item>
    </Menu>
  );

  const tableColumns = [
    {
      title: 'Kod',
      dataIndex: 'sourceId',
      key: 'sourceId',
    },
    {
      title: 'İsim',
      dataIndex: 'originalName',
      key: 'originalName',
    },
    {
      title: 'Gösterilen isim',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '',
      key: 'action',
      render: (_: any, location: Location) => (
        <div style={{ textAlign: 'right' }}>
          <Dropdown overlay={tableActions(location.id)} trigger={['click']}>
            <Button size="small" icon={<EllipsisOutlined />} />
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Lokasyonlar"
        extra={[
          <Button key="refresh" onClick={onRefreshClick} icon={<ReloadOutlined />}>
            Yenile
          </Button>,
        ]}
      />
      <Content>
        <Table columns={tableColumns} rowKey={row => row.id} dataSource={locations} loading={loading} pagination={false} />
      </Content>
      <Modal title="Lokasyon düzenle" visible={editModalOpen} footer={null} onCancel={closeEditModal} destroyOnClose={true}>
        <LocationEditPage location={editModalLocation} onSaved={onUserAdded} />
      </Modal>
      <Modal
        title="Değişim geçmişi"
        visible={changeLogModalOpen !== undefined}
        footer={null}
        onCancel={closeChangeLogModal}
        destroyOnClose={true}
        width={'80%'}
      >
        <EntityChangeLog entityName={'Location'} recordId={changeLogModalOpen} />
      </Modal>
    </>
  );
}
